import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Dot } from '@givebutter/primitives/dot';
import { maskDecimal } from '@givebutter/util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickableLineItem } from 'assets/js/pages/transactions/ClickableLineItem';
import { getProductIcon } from 'assets/js/pages/transactions/helpers';
import { transformTicketNumber } from 'components/pages/tickets/transform';
import * as Tooltip from 'components/tooltip';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';
import { Ticket, TransactionLineItem } from 'types/domain';

interface ITicketLineItemContext {
    ticket: Ticket;
    url: string;
}

const TicketLineItemContext = createContext<ITicketLineItemContext>({
    ticket: {} as Ticket,
    url: '',
});

interface TicketLineItemProps
    extends Pick<ITicketLineItemContext, 'ticket'>,
        ComponentPropsWithoutRef<'button'> {
    ticket: Ticket;
}

const TicketLineItem = (props: TicketLineItemProps) => {
    const { ticket, onClick, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { transaction_id: transactionId } = useParams<{ transaction_id?: string }>();
    const match = useRouteMatch();
    const [_, p] = match.path.split('/accounts/:account_id/');
    const [resource] = p.split('/');
    const nextUrl =
        resource === 'transactions'
            ? `/accounts/${accountId}/transactions/${transactionId}/tickets/${ticket.id}`
            : `/accounts/${accountId}/tickets/${ticket.id}`;
    const value = useMemo(() => ({ ticket, url: nextUrl }), [ticket, nextUrl]);

    return (
        <TicketLineItemContext.Provider value={value}>
            <ClickableLineItem
                onClick={e => {
                    history.push(nextUrl);
                    onClick?.(e);
                }}
                {...rest}
            />
        </TicketLineItemContext.Provider>
    );
};

interface TicketLineItemIconProps extends ComponentPropsWithoutRef<'div'> {}

const TicketLineItemIcon = (props: TicketLineItemIconProps) => {
    const { className, onClick, ...rest } = props;

    return (
        <div
            className={twMerge(
                'flex aspect-square h-12 items-center justify-center rounded bg-gray-200 bg-cover bg-center bg-no-repeat',
                className,
            )}
            {...rest}
        >
            <FontAwesomeIcon
                className={'text-gray-400'}
                size={'lg'}
                icon={getProductIcon({ subtype: 'ticket' } as TransactionLineItem)}
            />
        </div>
    );
};

interface TicketLineItemDetailsContainerProps extends ComponentPropsWithoutRef<'div'> {}

const TicketLineItemDetailsContainer = (props: TicketLineItemDetailsContainerProps) => {
    const { className, ...rest } = props;

    return <div className={twMerge('flex flex-grow gap-4', className)} {...rest} />;
};

interface TicketLineItemDetailsProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}

const TicketLineItemDetails = (props: TicketLineItemDetailsProps) => {
    const { className, ...rest } = props;
    const { ticket } = useContext(TicketLineItemContext);

    return (
        <div className={twMerge('flex flex-grow flex-col', className)} {...rest}>
            <p className={'whitespace-pre-wrap text-sm font-medium text-gray-800'}>
                {ticket.title} - {ticket.name}
            </p>
            <div className={'flex items-center gap-2'}>
                <p className={'text-sm text-gray-600'}>
                    {transformTicketNumber(ticket.uid_suffix)}
                </p>
                <div className={'flex items-center justify-center'}>
                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            <Dot className={getIndicatorClass(ticket)} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse={true}>
                                <p>{getStatusReadable(ticket)}</p>
                                <Tooltip.Arrow inverse={true} />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        </div>
    );
};

interface TicketLineItemPriceProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}

const TicketLineItemPrice = (props: TicketLineItemPriceProps) => {
    const { className, ...rest } = props;
    const currencySymbol = useCurrencySymbol();
    const { ticket, url } = useContext(TicketLineItemContext);
    const history = useHistory();
    const { taxDeductibilitySettings } = useFlags();

    return (
        <div className={twMerge('flex flex-col items-end', className)} {...rest}>
            <p className={'text-sm font-medium text-gray-600'}>
                {currencySymbol}
                {maskDecimal(ticket.price)}
            </p>
            {taxDeductibilitySettings && ticket.retail_price && !ticket.bundle_id && (
                <p className={'text-sm italic text-gray-500'}>
                    (Fair market value: {currencySymbol}
                    {maskDecimal(ticket.retail_price)})
                </p>
            )}
            <Button
                variant="none"
                color="primary"
                className="font-medium"
                size="sm"
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    history.push(url);
                }}
            >
                View
            </Button>
        </div>
    );
};

const getIndicatorClass = (ticket: Ticket) => {
    if (ticket.revoked_at) {
        return 'bg-red-500';
    }

    if (ticket.checked_in_at) {
        return 'bg-green-500';
    }

    return 'bg-yellow-500';
};

const getStatusReadable = (ticket: Ticket) => {
    if (ticket.revoked_at) {
        return 'Revoked';
    }

    if (ticket.checked_in_at) {
        return 'Checked in';
    }

    return 'Not checked in';
};

const Root = TicketLineItem;
const Icon = TicketLineItemIcon;
const DetailsContainer = TicketLineItemDetailsContainer;
const Details = TicketLineItemDetails;
const Price = TicketLineItemPrice;

export {
    TicketLineItem,
    TicketLineItemIcon,
    TicketLineItemDetailsContainer,
    TicketLineItemDetails,
    TicketLineItemPrice,
    //
    Root,
    Icon,
    DetailsContainer,
    Details,
    Price,
};
